import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import { Box } from "@mui/material";
import MuiAccordion, { type AccordionProps } from "@mui/material/Accordion";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import MuiAccordionSummary, {
  type AccordionSummaryProps,
  accordionSummaryClasses,
} from "@mui/material/AccordionSummary";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import * as React from "react";

const DashboardHeadbandContext = React.createContext<{ ariaLabel: string; $expandable: boolean }>({
  ariaLabel: "",
  $expandable: true,
});

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion
    disableGutters
    elevation={0}
    square
    slotProps={{ ...props.slotProps, heading: { component: "div" } }}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,
  marginBottom: theme.spacing(2),
  "&::before": {
    display: "none",
  },
}));

const AccordionSummary = styled(
  (props: AccordionSummaryProps & { $expandable?: boolean }) => (
    <MuiAccordionSummary
      component="div"
      role="button"
      expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem", color: "primary.main" }} />}
      {...props}
    />
  ),
  { shouldForwardProp: (prop) => prop !== "$expandable" }
)(({ theme, $expandable }) => ({
  minHeight: "55px",
  cursor: $expandable ? "pointer" : "default !important",
  [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
    order: -1,
    marginRight: theme.spacing(2),
    display: $expandable ? "inline-block" : "none",
  },
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
    transform: "rotate(90deg)",
  },
  [`& .${accordionSummaryClasses.content}`]: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const Header = ({ children }: { children: NonNullable<React.ReactNode> }) => {
  return <Box sx={{ display: "flex", alignItems: "center" }}>{children}</Box>;
};

const HeaderTitle = ({ children }: { children: React.ReactNode }) => {
  const { ariaLabel, $expandable } = React.useContext(DashboardHeadbandContext);

  return (
    <AccordionSummary aria-controls={`${ariaLabel}-content`} $expandable={$expandable}>
      <Typography
        component="span"
        sx={{ fontWeight: "bold", color: "primary.main", display: "flex", alignItems: "center" }}
      >
        {children}
      </Typography>
    </AccordionSummary>
  );
};

const HeaderButton = ({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) => {
  return (
    <Button variant="contained" size="small" onClick={onClick} sx={{ ml: 2, mr: 2 }}>
      {children}
    </Button>
  );
};

const Details = ({ children }: { children: React.ReactNode }) => {
  const { ariaLabel } = React.useContext(DashboardHeadbandContext);

  return (
    <AccordionDetails id={`${ariaLabel}-content`}>
      <Typography>{children}</Typography>
    </AccordionDetails>
  );
};

const DashboardHeadbandRoot = ({
  ariaLabel,
  initialExpanded = true,
  children,
}: {
  ariaLabel: string;
  initialExpanded?: boolean;
  children: NonNullable<React.ReactNode>;
}) => {
  const [expanded, toggleExpanded] = React.useReducer((prev: boolean) => !prev, initialExpanded);

  const expandable = React.useMemo(() => {
    return React.Children.toArray(children).some((child) => {
      if (React.isValidElement(child) && child.type === Details) {
        return true;
      }
      return false;
    });
  }, [children]);

  return (
    <DashboardHeadbandContext.Provider value={{ ariaLabel, $expandable: expandable }}>
      <Accordion expanded={expanded} onChange={expandable ? toggleExpanded : undefined}>
        {children}
      </Accordion>
    </DashboardHeadbandContext.Provider>
  );
};

export const DashboardHeadband = Object.assign(DashboardHeadbandRoot, {
  Header,
  HeaderTitle,
  HeaderButton,
  Details,
});
